import LoadingSpinner from '@/components/LoadingSpinner';
import { Providers } from '@/components/Providers';
import { CheckoutPage } from '@/pages';
import { matchRoute } from '@tagadapay/plugin-sdk/v2';
import { Suspense, lazy } from 'react';
import { Redirect, Route, Router, Switch } from 'wouter';

/** ClubApp lazy-loaded -- separate sub-app only needed at /club/* */
const ClubApp = lazy(() => import('@/club/ClubApp'));

/** PortalApp lazy-loaded -- separate sub-app only needed at /portal/* */
const PortalApp = lazy(() => import('@/portal/PortalApp'));

/** ThankYou + Offer pages: lazy-loaded — only needed after purchase / on offer routes */
const ThankYouPage = lazy(() => import('@/pages/ThankYouPage').then(m => ({ default: m.ThankYouPage })));
const OfferFromStaticPage = lazy(() => import('@/pages/OfferPage').then(m => ({ default: m.OfferFromStaticPage })));
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage').then(m => ({ default: m.NotFoundPage })));

/**
 * Remapped Routes Handler
 * Handles dynamic path remapping for all remappable routes
 * Uses matchRoute from SDK to match internal paths to external URLs
 */
function RemappedRoutes() {
  // Check checkout route (remappable)
  const checkoutMatch = matchRoute('/checkout');
  if (checkoutMatch.matched) {
    return <CheckoutPage />;
  }

  // Check offer route (remappable)
  const offerMatch = matchRoute('/offer');
  if (offerMatch.matched) {
    return <OfferFromStaticPage />;
  }

  // Check thank you route (remappable)
  const thankyouMatch = matchRoute('/thankyou/:orderId');
  if (thankyouMatch.matched && thankyouMatch.params?.orderId) {
    return <ThankYouPage params={{ orderId: thankyouMatch.params.orderId }} />;
  }

  return <NotFoundPage />;
}

/**
 * Main App Component
 * Handles routing and provides global context
 */
function App() {
  return (
    <Providers>
      <Router>
        <Switch>
          {/* Club routes - separate app (lazy-loaded) */}
          <Route path="/club/:rest*">
            <Suspense fallback={<LoadingSpinner />}>
              <ClubApp />
            </Suspense>
          </Route>
          <Route path="/club">
            <Redirect to="/club/enter" />
          </Route>

          {/* Portal routes - separate app (lazy-loaded) */}
          <Route path="/portal/:rest*">
            <Suspense fallback={<LoadingSpinner />}>
              <PortalApp />
            </Suspense>
          </Route>
          <Route path="/portal">
            <Redirect to="/portal/login" />
          </Route>

          {/* Main checkout routes (direct paths - not remapped) */}
          <Route path="/checkout">
            <Suspense fallback={<LoadingSpinner />}>
              <CheckoutPage />
            </Suspense>
          </Route>

          <Route path="/offer">
            <Suspense fallback={<LoadingSpinner />}>
              <OfferFromStaticPage />
            </Suspense>
          </Route>

          <Route path="/thankyou/:orderId">
            {(params) => (
              <Suspense fallback={<LoadingSpinner />}>
                <ThankYouPage params={params} />
              </Suspense>
            )}
          </Route>

          {/* Catch-all for remapped paths (handles external URL -> internal path mapping) */}
          <Route path="*">
            <Suspense fallback={<LoadingSpinner />}>
              <RemappedRoutes />
            </Suspense>
          </Route>
        </Switch>
      </Router>
    </Providers>
  );
}

export default App;
